import { inject, Injectable } from '@angular/core';
import { OnInitEffects, Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select, Action } from '@ngrx/store';
import { combineLatest, of, empty } from 'rxjs';
import { AppState } from '../core.state';
import { map, mergeMap, skipWhile, withLatestFrom, tap, catchError, switchMap, filter, take } from 'rxjs/operators';

import * as SavedRepliesActions from './saved.replies.actions'
import { SavedRepliesService } from '../services/saved.replies.service';
import { currentUser, currentOrganization } from '../auth';
import { ToastrService } from '@iqx-limited/ngx-toastr';
import * as ToastrActions  from './toastr.actions';
import { HttpErrorResponse } from '@angular/common/http';
import { cloneDeep } from 'lodash-es';
import { I18nService } from '../i18n';

@Injectable()
export class SavedRepliesEffects implements OnInitEffects  {
    private i18n = inject(I18nService);

    loadSavedReplies = createEffect(() => this.actions$.pipe(
        ofType(SavedRepliesActions.SavedRepliesRequested),
        mergeMap(action => this.store.pipe(
            select(currentUser),
            filter(u => u != null),
            take(1)
        )),
        switchMap( user => 
            this.savedRepliesService.getSavedReplies( user.organizations[0].id )
        ),
        map( (s) => SavedRepliesActions.SavedRepliesLoaded({SaveReplies: s.model}) )
    ));

    saveSavedReply = createEffect(() => this.actions$.pipe(
        ofType(SavedRepliesActions.SavedReplySaveRequested),
        withLatestFrom(this.store.pipe(select(currentOrganization))),
        mergeMap( ([action, org]) => {
            let sr = cloneDeep(action.SavedReply);
            sr.organization_id = org.id;
            return this.savedRepliesService.addSavedReply(sr).pipe(
                switchMap(res => [
                    SavedRepliesActions.SavedReplySaveSucess({SavedReply: res.model}),
                    ToastrActions.showToastrSuccess({message:this.i18n.t('core.saved_replies.saved_reply_has_been_successfully_saved', 'Saved Reply has been successfully saved.'), title: ''})
                ]),
                catchError((error: HttpErrorResponse) => of(
                    SavedRepliesActions.SavedReplySaveFailed({message: error.error}),
                    ToastrActions.showToastrError({message: error.error , title: this.i18n.t('common.something_went_wrong', 'Something went wrong')})
                ))
            );
        })
    ));

    deleteSavedReply = createEffect(() => this.actions$.pipe(
        ofType(SavedRepliesActions.SavedReplyDeleteRequested),
        mergeMap( action => 
            this.savedRepliesService.deleteSavedReply(action.id).pipe(
                switchMap(res => [
                    SavedRepliesActions.SavedReplyDeleteSucess({ id: action.id }),
                    ToastrActions.showToastrSuccess({message:this.i18n.t('core.saved_replies.saved_reply_has_been_successfully', 'Saved Reply has been successfully deleted'), title: ''})
                ]),
                catchError((error: HttpErrorResponse) => of(
                    SavedRepliesActions.SavedReplyDeleteFailed({message: error.error}),
                    ToastrActions.showToastrError({message: error.error , title: this.i18n.t('common.something_went_wrong', 'Something went wrong')})
                ))
            )
        )
    ));

    ngrxOnInitEffects(): Action {
        return { type: SavedRepliesActions.SavedRepliesRequested.type };
    }
    
    constructor(
        private savedRepliesService: SavedRepliesService,
        private actions$: Actions,
        private store: Store<AppState>,
        private toastr: ToastrService
    ) { }

}